import React, { useMemo } from "react";
import { calculateShadingForNumbers } from "../../../Lumber/TableShader";
import styles from "./styles.module.css";

/*
 * Summary rows shown above the bar and box-whisker charts, one column per zone/treatment pair
 * in the same order as the traces below. Graph.tsx owns the plot layout and passes its margins
 * and group gap in, which is what keeps each column sitting over its box.
 */

export interface DataTableColumn {
	id: string;
	acreage: string;
	plotCount: string;
	average: string;
	// Raw mean behind the `average` string, or null when the column has no data--drives the shading.
	averageValue: number | null;
}

export interface DataTableZone {
	id: string;
	columns: DataTableColumn[];
}

const ROWS = [
	{ key: "acreage", label: "Acreage" },
	{ key: "plotCount", label: "Plot Count" },
	{ key: "average", label: "Average" },
] as const;

/*
 * Gradient color for the average row, as a swappable token. Matches the shaded tables in
 * protocol and trial analysis
 */
const AVERAGE_SHADE_COLOR = "var(--data-positive)";

// Mixed fill for a cell at the given shading opacity
const shadeFill = (opacity: string) => `color-mix(in srgb, ${AVERAGE_SHADE_COLOR} ${opacity}, transparent)`;

// Background plus a readable text color, skipping the text override where there is no fill
const shadeStyle = (opacity: string): React.CSSProperties => ({
	backgroundColor: shadeFill(opacity),
	color: opacity === "0%" ? undefined : `contrast-color(${shadeFill(opacity)})`,
});

interface Props {
	zones: DataTableZone[];
	// Fraction of each zone slot left empty, matching the chart's boxgap/bargap
	groupGap: number;
	// Plot margins in px, so the outer columns line up with the chart's plotting area
	marginLeft: number;
	marginRight: number;
}

const DataTable = ({ zones, groupGap, marginLeft, marginRight }: Props) => {
	/*
	 * Shading normalized across every cell of the average row at once--all zones and treatments
	 * share one min/max, so the gradient reads as a single comparison. Columns without an average
	 * are left out of the range entirely and render unshaded.
	 */
	const averageOpacities = useMemo(() => {
		const shadeable = zones
			.flatMap((zone) => zone.columns)
			.filter((column): column is DataTableColumn & { averageValue: number } => column.averageValue !== null);

		const shading = calculateShadingForNumbers(shadeable.map((column) => column.averageValue)) as {
			opacity: string;
		}[];

		return new Map(shadeable.map((column, index) => [column.id, shading[index].opacity]));
	}, [zones]);

	if (zones.length === 0) return null;

	const zonePadding = `0 ${(groupGap / 2) * 100}%`;

	return (
		<div
			className={styles.dataTable}
			style={{ gridTemplateColumns: `${marginLeft}px repeat(${zones.length}, minmax(0, 1fr)) ${marginRight}px` }}
		>
			{ROWS.map(({ key, label }, rowIndex) => {
				const divider = rowIndex < ROWS.length - 1 ? styles.dataTableDivider : "";

				return (
					<React.Fragment key={key}>
						<div className={`${styles.dataTableLabel} ${divider}`}>{label}</div>
						{zones.map((zone) => (
							<div
								key={zone.id}
								className={`${styles.dataTableZone} ${divider}`}
								style={{
									gridTemplateColumns: `repeat(${zone.columns.length}, minmax(0, 1fr))`,
									padding: zonePadding,
								}}
							>
								{zone.columns.map((column) => {
									const opacity = key === "average" ? averageOpacities.get(column.id) : undefined;

									return (
										<div
											key={column.id}
											className={styles.dataTableCell}
											style={opacity === undefined ? undefined : shadeStyle(opacity)}
										>
											<span className={styles.dataTableValue}>{column[key]}</span>
										</div>
									);
								})}
							</div>
						))}
						{/* Fills the right-margin track so the next row starts back at the label column */}
						<div className={divider} />
					</React.Fragment>
				);
			})}
		</div>
	);
};

export default DataTable;
